import { FormEvent, useState } from 'react'
import { CreateInviteBody, useCreateInvite, useInvites } from 'src/hooks/useAdminAffiliates'
import { formatDay } from 'src/utils/affiliateFormat'
import { copyText } from 'src/utils/clipboard'
import { notify } from 'src/utils/notify'
import { errorMessage } from '../affiliate/labels'
import {
    Badge,
    Banner,
    Card,
    CardTitle,
    EmptyRow,
    ErrorText,
    Grid,
    InlineSpinner,
    Input,
    Label,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    SecondaryButton,
    Table,
    TableWrap,
} from '../affiliate/ui'
import { Mono, SmallButtonStyle, formMessage, readDollars, readInt, readPercent } from './shared'

const copy = (url: string) =>
    copyText(url).then(
        () => notify('Invite link copied.', 'success'),
        () => notify('Could not copy. Select the link and copy it manually.', 'error')
    )

const CreateInviteForm = () => {
    const create = useCreateInvite()
    const [email, setEmail] = useState('')
    const [firstMonth, setFirstMonth] = useState('')
    const [recurring, setRecurring] = useState('')
    const [threshold, setThreshold] = useState('')
    const [expiresInDays, setExpiresInDays] = useState('14')
    const [created, setCreated] = useState<{ url: string; expiresAt: string } | null>(null)

    const submit = (e: FormEvent) => {
        e.preventDefault()
        let body: CreateInviteBody
        try {
            body = {
                email: email.trim() || undefined,
                firstMonthBps: readPercent(firstMonth, 'First-month rate'),
                recurringBps: readPercent(recurring, 'Recurring rate'),
                minPayoutCents: readDollars(threshold, 'Payout threshold'),
                expiresInDays: readInt(expiresInDays, 'Expires in days', 1, 90),
            }
        } catch (err) {
            notify(formMessage(err) || 'Check the form.', 'error')
            return
        }
        create.mutate(body, {
            onSuccess: (invite) => {
                setCreated(invite)
                notify('Invite created.', 'success')
            },
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Create invite</CardTitle>
            <Muted>Invited users skip the application. Leave rates blank to use the program defaults.</Muted>
            <Grid $min={180}>
                <Label>
                    Email (optional)
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="anyone" />
                </Label>
                <Label>
                    First month %
                    <Input inputMode="decimal" value={firstMonth} onChange={(e) => setFirstMonth(e.target.value)} placeholder="default" />
                </Label>
                <Label>
                    Recurring %
                    <Input inputMode="decimal" value={recurring} onChange={(e) => setRecurring(e.target.value)} placeholder="default" />
                </Label>
                <Label>
                    Payout threshold $
                    <Input inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="default" />
                </Label>
                <Label>
                    Expires in days
                    <Input inputMode="numeric" value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} />
                </Label>
            </Grid>
            <Row>
                <PrimaryButton type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Creating…' : 'Create invite'}
                </PrimaryButton>
            </Row>
            {created && (
                <Banner $tone="success">
                    <p style={{ overflowWrap: 'anywhere' }}>
                        {created.url}
                        <br />
                        <Muted as="span">Expires {formatDay(created.expiresAt)}</Muted>
                    </p>
                    <SecondaryButton type="button" onClick={() => copy(created.url)}>
                        Copy
                    </SecondaryButton>
                </Banner>
            )}
        </Card>
    )
}

const InvitesList = () => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error } = useInvites(page)
    const now = Date.now()

    return (
        <Card>
            <CardTitle>Invites</CardTitle>
            {isLoading ? (
                <InlineSpinner />
            ) : error ? (
                <ErrorText>{errorMessage(error)}</ErrorText>
            ) : (
                <>
                    <TableWrap>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>State</th>
                                    <th>Created</th>
                                    <th>Expires</th>
                                    <th>Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.results.length > 0 ? (
                                    data.results.map((invite) => {
                                        const expired = new Date(invite.expiresAt).getTime() < now
                                        return (
                                            <tr key={invite.id}>
                                                <td style={{ overflowWrap: 'anywhere' }}>{invite.email || <Muted as="span">Anyone</Muted>}</td>
                                                <td>
                                                    {invite.acceptedBy ? (
                                                        <Badge $tone="green">Accepted</Badge>
                                                    ) : expired ? (
                                                        <Badge $tone="red">Expired</Badge>
                                                    ) : (
                                                        <Badge $tone="blue">Open</Badge>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatDay(invite.createdAt)}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{formatDay(invite.expiresAt)}</td>
                                                <td>
                                                    <Row $wrap={false}>
                                                        <Mono style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'normal' }}>
                                                            {invite.url}
                                                        </Mono>
                                                        <SecondaryButton
                                                            style={SmallButtonStyle}
                                                            disabled={!!invite.acceptedBy || expired}
                                                            onClick={() => copy(invite.url)}
                                                        >
                                                            Copy
                                                        </SecondaryButton>
                                                    </Row>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <EmptyRow colSpan={5}>No invites yet.</EmptyRow>
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                    {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
                </>
            )}
        </Card>
    )
}

const InvitesTab = () => (
    <>
        <CreateInviteForm />
        <InvitesList />
    </>
)

export default InvitesTab
